// Directo a PostHog (no por utils/analytics): estas pantallas son públicas o
// del alta, y la fachada arrastra la inicialización de Firebase.
import i18next from "i18next";
import { capturePostHogEvent } from "../posthog";

/**
 * Embudo de captación en la consola (contrato PostHog §3, CC-CONSOLE#1538):
 * registro, bienvenida, vincular dispositivo y tienda pública.
 *
 * Regla de privacidad de este archivo: aquí NO viajan nombres, correos,
 * teléfonos, empresas, contraseñas, IMEI completos ni datos de la tarjeta.
 * Sólo pasos, enumeraciones cerradas, ids técnicos, cantidades y montos. Las
 * firmas están cerradas a propósito para que un llamador no pueda colar un
 * dato personal.
 */

type Params = Record<string, string | number | boolean>;

const send = (name: string, params: Params): void => {
  capturePostHogEvent(name, params);
};

// --- Registro -----------------------------------------------------------------

export type RegisterStep =
  | "account_form"
  | "account_created"
  | "google_started"
  | "company_form"
  | "company_created"
  | "personal_account"
  | "plan_selected"
  | "checkout_redirect";

/** Paso del alta. `intent` y `plan` son los ids de la landing (`app`, `gps-propio`, slug). */
export const trackRegisterStep = (
  step: RegisterStep,
  extra: { intent?: string; plan?: string; method?: "email" | "google" } = {},
): void => {
  send("register_step", {
    step,
    ...(extra.intent ? { intent: extra.intent } : {}),
    ...(extra.plan ? { plan: extra.plan } : {}),
    ...(extra.method ? { method: extra.method } : {}),
  });
};

/** El formulario llegó prellenado desde la landing: sólo QUÉ campos, nunca sus valores. */
export const trackRegisterPrefillUsed = (fields: { name?: boolean; email?: boolean; phone?: boolean; company?: boolean; intent?: string }): void => {
  send("register_prefill_used", {
    has_name: Boolean(fields.name),
    has_email: Boolean(fields.email),
    has_phone: Boolean(fields.phone),
    has_company: Boolean(fields.company),
    ...(fields.intent ? { intent: fields.intent } : {}),
  });
};

// --- Alta manual gratis directa (CC-CONSOLE#1550) ------------------------------

export type SignupVerificationChannel = "email" | "whatsapp" | "sms";

/** Se mandó un código de verificación del alta (nunca el código ni el destino). */
export const trackSignupVerificationSent = (channel: SignupVerificationChannel): void => {
  send("signup_verification_sent", { channel });
};

/** Se confirmó el correo o el teléfono con el código. */
export const trackSignupVerificationCompleted = (channel: SignupVerificationChannel): void => {
  send("signup_verification_completed", { channel });
};

/**
 * El alta quedó con la licencia gratis sin elegirla. `signup_clicks`: clics
 * desde que abrió el formulario (envío incluido), para medir la ruta corta.
 */
export const trackSignupFreeLicenseAuto = (extra: { method: "email" | "google"; personal: boolean; signupClicks?: number }): void => {
  send("signup_free_license_auto", {
    method: extra.method,
    personal_account: extra.personal,
    ...(typeof extra.signupClicks === "number" ? { signup_clicks: extra.signupClicks } : {}),
  });
};

/** «Ver mi mapa con datos de ejemplo» en la bienvenida. */
export const trackWelcomeQuickMap = (): void => {
  send("welcome_quick_map", {});
};

/** Primera vez que abre el mapa tras el alta (con el Sandbox encendido o no). */
export const trackFirstMapView = (extra: { sandbox: boolean }): void => {
  send("first_map_view", { sandbox: extra.sandbox });
};

// --- Bienvenida ---------------------------------------------------------------

export const trackOnboardingStep = (step: string, extra: { action?: string } = {}): void => {
  send("onboarding_step", { step: step.slice(0, 60), ...(extra.action ? { action: extra.action.slice(0, 60) } : {}) });
};

// --- Vincular dispositivo ---------------------------------------------------------

export type DeviceLinkKind = "claim" | "own";

/** Desde dónde se vincula: el paso a paso (`/dispositivos/vincular`) o el alta de `/admin/devices`. */
export type DeviceLinkFrom = "wizard" | "admin";

/**
 * Eligió el camino. `kind` conserva sus valores históricos (`claim` | `own`),
 * igual que `device_link_result` y `support_ticket_from_link`; CC-CONSOLE#1561
 * agrega `path` (`wayak` | `third_party`, el nombre que ve la persona en
 * /admin/devices) y `from` (`wizard` | `admin`).
 */
export const trackDeviceLinkStarted = (kind: DeviceLinkKind, from: DeviceLinkFrom = "wizard"): void => {
  send("device_link_started", { kind, path: kind === "claim" ? "wayak" : "third_party", from });
};

/**
 * Resultado visto por la persona. `errorCode` es el código estable del API
 * (`DEVICE_NOT_CLAIMABLE`…), nunca el mensaje. `imei` se reduce aquí a sus
 * últimos 4 dígitos.
 */
export const trackDeviceLinkResult = (
  kind: DeviceLinkKind,
  outcome: { ok: boolean; errorCode?: string; httpStatus?: number; imei?: string; sandboxEnabled?: boolean },
): void => {
  const digits = (outcome.imei || "").replace(/\D/g, "");
  send("device_link_result", {
    kind,
    ok: outcome.ok,
    ...(outcome.errorCode ? { error_code: outcome.errorCode.slice(0, 60) } : {}),
    ...(outcome.httpStatus ? { http_status: outcome.httpStatus } : {}),
    ...(digits ? { imei_last4: digits.slice(-4) } : {}),
    ...(outcome.sandboxEnabled !== undefined ? { sandbox_enabled: outcome.sandboxEnabled } : {}),
  });
};

/**
 * Abrió `/dispositivos/vincular` sin sesión y se le mandó al alta
 * (CC-CONSOLE#1561). `has_account_hint`: si había señales de una cuenta previa
 * en este navegador (hoy siempre false: no se adivina).
 */
export const trackDeviceLinkAuthRequired = (extra: { hasAccountHint: boolean } = { hasAccountHint: false }): void => {
  send("device_link_auth_required", { has_account_hint: extra.hasAccountHint });
};

export const trackSupportTicketFromLink = (kind: DeviceLinkKind | "status", reason?: string): void => {
  send("support_ticket_from_link", { kind, ...(reason ? { reason: reason.slice(0, 60) } : {}) });
};

// --- Tienda pública ----------------------------------------------------------------
// Todos los eventos `store_*` llevan `lang` (es | en): el idioma en que la
// persona ve la tienda (i18next de la v5; lo que no es inglés cuenta como es).

const storeLang = (): "es" | "en" => (/^en(?:$|[-_])/i.test(String(i18next.language || "")) ? "en" : "es");
const sendStore = (name: string, params: Params): void => send(name, { ...params, lang: storeLang() });

export const trackStoreViewed = (storeSlug: string, page: "home" | "catalog" | "product" | "page" | "cart" | "checkout" | "success" | "quote" | "pickup"): void => {
  sendStore("store_viewed", { store_slug: storeSlug.slice(0, 80), page });
};

export const trackStoreProductViewed = (storeSlug: string, product: { id: string; price?: number; category?: string | null }): void => {
  sendStore("store_product_viewed", {
    store_slug: storeSlug.slice(0, 80),
    product_id: product.id,
    ...(typeof product.price === "number" ? { price: product.price } : {}),
    ...(product.category ? { category: String(product.category).slice(0, 60) } : {}),
  });
};

export const trackStoreAddToCart = (storeSlug: string, item: { productId: string; quantity: number; price?: number }): void => {
  sendStore("store_add_to_cart", {
    store_slug: storeSlug.slice(0, 80),
    product_id: item.productId,
    quantity: item.quantity,
    ...(typeof item.price === "number" ? { price: item.price } : {}),
  });
};

export const trackStoreCheckoutStarted = (storeSlug: string, cart: { itemCount: number; units: number; subtotal: number; hasCoupon: boolean }): void => {
  sendStore("store_checkout_started", {
    store_slug: storeSlug.slice(0, 80),
    item_count: cart.itemCount,
    units: cart.units,
    subtotal: cart.subtotal,
    has_coupon: cart.hasCoupon,
  });
};

// --- Cotización dentro de la tienda (CC-CONSOLE #1544) ----------------------
// `store_quote_requested` lo manda ahora el API (con folio y uuid determinista)
// al guardar la solicitud: el cliente ya no lo dispara al agregar un producto.

/** Un producto por cotizar (sin precio publicado) entró al carrito de cotización. */
export const trackStoreQuoteItemAdded = (storeSlug: string, item: { productId: string; quantity: number }): void => {
  sendStore("store_quote_item_added", {
    store_slug: storeSlug.slice(0, 80),
    product_id: item.productId,
    quantity: item.quantity,
  });
};

/** Abrió el formulario de cotización con su carrito. */
export const trackStoreQuoteStarted = (storeSlug: string, cart: { items: number; units: number; hasUnpriced: boolean }): void => {
  sendStore("store_quote_started", {
    store_slug: storeSlug.slice(0, 80),
    items: cart.items,
    units: cart.units,
    has_unpriced: cart.hasUnpriced,
  });
};

/** La solicitud quedó guardada: folio (no es dato personal), renglones y unidades. */
export const trackStoreQuoteSubmitted = (
  storeSlug: string,
  quote: { folio: string; items: number; units: number; hasUnpriced: boolean },
): void => {
  sendStore("store_quote_submitted", {
    store_slug: storeSlug.slice(0, 80),
    folio: quote.folio.slice(0, 40),
    items: quote.items,
    units: quote.units,
    has_unpriced: quote.hasUnpriced,
  });
};
